import { Button, Col, Icon, Row, Spinner2, Text } from 'components';
import { Api, theme } from 'lib';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Option, Radio, ResultBanner, SurfaceCard } from './surfaceStyles';

// Backend quiz states (mirrors QuickQuiz/QuizCard QUIZ_STATE).
const READY = 'ready_to_play';
const LOCKED = 'locked';
const COMING_UP = 'coming_up';

/**
 * Inline half-time quiz card for the Team Channel surface (fan view).
 *
 * Only HALF_TIME quizzes ever reach the surface, and only while READY_TO_PLAY or
 * COMPLETED. The card renders off the surface payload; when the quiz is already
 * answered it hydrates /quiz/get once to pull the fan's pick + correctness for the
 * result view. After answering we re-fetch so the correct answer can be highlighted.
 */
const SurfaceQuizCard = ({ quiz, onAnswered }) => {
  const dispatch = useDispatch();
  const [fullQuiz, setFullQuiz] = useState(null); // answers (with isCorrect once revealed)
  const [result, setResult] = useState(null); // { isCorrect, earnedPoints, selectedAnswerId }
  const [selectedId, setSelectedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [hydrating, setHydrating] = useState(false);
  const [err, setErr] = useState(null);

  const quizId = quiz?._id;
  const state = quiz?.state;
  const answered = quiz?.answered === true;

  // Render from the surface payload by default; hydrate the answered detail (user's pick,
  // points, correctness) only when the fan has already answered.
  useEffect(() => {
    if (!quizId) return;
    setSelectedId(null);
    setResult(null);
    setErr(null);
    if (!answered) {
      setFullQuiz(quiz);
      return;
    }
    let cancelled = false;
    setHydrating(true);
    (async () => {
      const res = await Api.get(`/quiz/get/${quizId}`, null, { silent: true });
      if (cancelled) return;
      setFullQuiz(res?.quiz || quiz);
      if (res?.answered && res.userResponse) {
        setResult({
          isCorrect: res.userResponse.isCorrect,
          earnedPoints: res.userResponse.earnedPoints,
          selectedAnswerId: res.userResponse.selectedAnswerId
        });
      }
      setHydrating(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [quizId, answered, quiz]);

  const correctAnswerId = useMemo(() => {
    if (!result || !fullQuiz?.answers) return null;
    const correct = fullQuiz.answers.find((a) => a.isCorrect);
    return correct?._id ? String(correct._id) : null;
  }, [result, fullQuiz]);

  if (!quiz) return null;

  const handleSubmit = async () => {
    if (!selectedId || !quizId || submitting) return;
    setSubmitting(true);
    setErr(null);
    const res = await Api.post(
      '/quiz/answer',
      { quizId, answerId: selectedId },
      null,
      { silent: true }
    );
    setSubmitting(false);
    if (res?.err) {
      setErr(res.err);
      dispatch({ type: 'HANDLE_RES', payload: { err: res.err } });
      return;
    }
    // Re-fetch the full quiz so we get the answer-correctness map for the result UI.
    const refreshed = await Api.get(`/quiz/get/${quizId}`, null, { silent: true });
    if (refreshed?.quiz) setFullQuiz(refreshed.quiz);
    setResult({
      isCorrect: !!res.isCorrect,
      earnedPoints: res.earnedPoints || 0,
      selectedAnswerId: selectedId
    });
    dispatch({ type: 'HANDLE_RES', payload: { res: res.isCorrect ? 'Correct!' : 'Submitted.' } });
    onAnswered && onAnswered(quizId, { isCorrect: !!res.isCorrect, earnedPoints: res.earnedPoints || 0 });
  };

  const isOpen = state === READY && !result;
  const answers = fullQuiz?.answers || quiz.answers || [];

  // Locked / window closed and the fan never answered → no results to show.
  const closedUnanswered = (state === LOCKED || state === COMING_UP) && !result && !answered;

  return (
    <SurfaceCard>
      <Row noFlex center gap="8px" marg="0 0 6px">
        <Text c2 col={theme.SECONDARY} bold>
          Half-time quiz
        </Text>
        {state === READY && !result && (
          <Text c2 bold col={theme.GREEN}>
            · Live
          </Text>
        )}
      </Row>

      {hydrating ? (
        <Col centerAll pad="24px 0">
          <Spinner2 col={theme.SECONDARY} />
        </Col>
      ) : closedUnanswered ? (
        <>
          <Text h4 marg="0 0 8px">
            {quiz.question}
          </Text>
          <Text c2 col={theme.GREY_50}>
            {state === COMING_UP ? 'Quiz opens at half-time.' : 'Quiz Closed'}
          </Text>
        </>
      ) : (
        <>
          {result && (
            <ResultBanner correct={result.isCorrect}>
              <Text h4 col={result.isCorrect ? theme.GREEN : theme.SECONDARY}>
                {result.isCorrect ? "That's correct!" : 'Not quite.'}
              </Text>
              <Text b2 col={theme.GREY_70}>
                {result.earnedPoints > 0
                  ? `You earned ${result.earnedPoints} pts.`
                  : 'Better luck next time.'}
              </Text>
            </ResultBanner>
          )}

          <Text h4 marg="0 0 14px">
            {quiz.question}
          </Text>

          {answers.map((a) => {
            const id = String(a._id);
            const selected = selectedId === id || result?.selectedAnswerId === id;
            let optState = null;
            if (result) {
              if (correctAnswerId && id === correctAnswerId) optState = 'correct';
              else if (result.selectedAnswerId === id && !result.isCorrect) optState = 'wrong';
            }
            return (
              <Option
                key={id}
                type="button"
                selected={selected}
                state={optState}
                disabled={!isOpen}
                onClick={() => isOpen && setSelectedId(id)}
              >
                <Radio selected={selected} state={optState}>
                  {optState === 'correct' && <Icon type="check" dimensions={10} col="#fff" />}
                  {optState === 'wrong' && <Icon type="close" dimensions={8} col="#fff" />}
                </Radio>
                <span>{a.text}</span>
              </Option>
            );
          })}

          {err && !result && (
            <Text c2 col={theme.SECONDARY} marg="0 0 12px">
              {err}
            </Text>
          )}

          {isOpen && (
            <Row between center marg="14px 0 0">
              <Text c2 col={theme.GREY_60}>
                {quiz.rewardPoints || 0} pts available
              </Text>
              <Button
                text={submitting ? 'Submitting…' : 'Submit'}
                onClick={handleSubmit}
                disabled={!selectedId || submitting}
                bg={theme.SECONDARY}
                style={{ width: 'fit-content' }}
              />
            </Row>
          )}
        </>
      )}
    </SurfaceCard>
  );
};

export default SurfaceQuizCard;
